import { useTranslation } from 'react-i18next';
import type { UploadSlot } from '@/components/contentPane/useComposerUploads';
import { Tooltip } from '@/components/Tooltip';
import { CheckIcon, CloseIcon, PaperclipIcon } from '@/components/icons';

/** Pending-attachment UI above the composer: the "send as file / group"
 *  options row plus the thumbnail (image) / chip (file) preview strip with
 *  per-slot progress, error and remove affordances. */
export function UploadPreviewStrip(props: {
  uploads: UploadSlot[];
  groupAttachments: boolean;
  onSetAllAsFile: (value: boolean) => void;
  onToggleGroup: () => void;
  onToggleAsFile: (fileId: string) => void;
  onCancelUpload: (fileId: string) => void;
}) {
  const { t } = useTranslation();
  const { uploads } = props;
  if (uploads.length === 0) return null;

  const imageSlots = uploads.filter((u) => u.previewUrl);
  const allAsFile = imageSlots.length > 0 && imageSlots.every((u) => u.asFile);

  return (
    <>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
        {imageSlots.length > 0 && (
          <button
            type="button"
            onClick={() => props.onSetAllAsFile(!allAsFile)}
            className="flex items-center gap-1.5 transition-colors hover:text-foreground"
          >
            <span
              className={
                'grid h-3.5 w-3.5 place-items-center rounded border ' +
                (allAsFile ? 'border-accent bg-accent text-white' : 'border-ink-muted/50 text-transparent')
              }
            >
              <CheckIcon className="h-2.5 w-2.5" />
            </span>
            {t('ui.upload.sendAsFile', { count: imageSlots.length })}
          </button>
        )}
        {uploads.length > 1 && (
          // Grouping is the silent default (one message with the album, like
          // Telegram). The rare "send each separately" is a quiet secondary
          // link, not a checkbox — no decision is forced on the common path.
          <button
            type="button"
            onClick={props.onToggleGroup}
            className="text-ink-muted underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            {props.groupAttachments ? t('ui.upload.sendSeparately') : t('ui.upload.combine')}
          </button>
        )}
      </div>
      <div className="mb-1 flex flex-wrap gap-1.5">
        {uploads.map((u) => {
          const progressPct = u.size > 0 ? Math.round((u.loaded / u.size) * 100) : 0;
          const uploading = u.status === 'uploading' || u.status === 'preparing';

          // Images get a Telegram-style thumbnail tile; everything else a
          // compact file chip with a download-able name (the sent bubble
          // renders images inline and files as a download chip).
          if (u.previewUrl) {
            return (
              <div
                key={u.fileId}
                className={
                  'group/upl relative h-16 w-16 overflow-hidden rounded-xl border ' +
                  (u.status === 'error' ? 'border-danger/50' : 'border-separator')
                }
                title={u.error ?? u.name}
              >
                <img
                  src={u.previewUrl}
                  alt={u.name}
                  className={'h-full w-full object-cover ' + (u.asFile ? 'opacity-40' : '')}
                />
                {uploading && (
                  <div className="absolute inset-0 grid place-items-center bg-black/40 font-mono text-[10px] text-white">
                    <span className="tabular-nums">{progressPct}%</span>
                  </div>
                )}
                {u.status === 'error' && (
                  <div className="absolute inset-0 grid place-items-center bg-danger/30 text-[10px] font-medium text-white">
                    err
                  </div>
                )}
                {u.asFile && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[9px] font-medium text-white">
                    {t('ui.upload.asFileBadge')}
                  </div>
                )}
                <Tooltip label={u.asFile ? t('ui.upload.sendAsImage') : t('ui.upload.sendAsFileSingle')}>
                  <button
                    type="button"
                    onClick={() => props.onToggleAsFile(u.fileId)}
                    aria-pressed={u.asFile}
                    className={
                      'absolute left-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-lg text-white transition-colors ' +
                      (u.asFile ? 'bg-accent' : 'bg-black/55 hover:bg-black/75')
                    }
                  >
                    <PaperclipIcon className="h-3 w-3" />
                  </button>
                </Tooltip>
                <Tooltip label={t('ui.upload.remove')}>
                  <button
                    type="button"
                    onClick={() => props.onCancelUpload(u.fileId)}
                    className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-lg bg-black/55 text-white transition-colors hover:bg-black/75"
                  >
                    <CloseIcon className="h-3 w-3" />
                  </button>
                </Tooltip>
              </div>
            );
          }

          return (
            <div
              key={u.fileId}
              className={
                'flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ' +
                (u.status === 'error' ? 'ring-1 ring-danger/40' : '')
              }
              style={{
                backgroundColor: 'rgb(var(--surface-card) / 0.6)',
                backdropFilter: 'blur(6px) saturate(140%)',
                WebkitBackdropFilter: 'blur(6px) saturate(140%)',
              }}
              title={u.error ?? u.name}
            >
              <PaperclipIcon className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
              <span className="max-w-[180px] truncate text-ink-dim">{u.name}</span>
              {uploading && (
                <span className="font-mono text-[10px] tabular-nums text-accent">{progressPct}%</span>
              )}
              {u.status === 'done' && (
                <span className="font-medium text-[10px] leading-none !text-success-dim">✓</span>
              )}
              {u.status === 'error' && (
                <span className="font-medium text-[10px] leading-none !text-danger-ink">err</span>
              )}
              <Tooltip label={t('ui.upload.cancel')}>
                <button
                  type="button"
                  onClick={() => props.onCancelUpload(u.fileId)}
                  className="shrink-0 rounded p-0.5 text-ink-muted transition-colors hover:bg-white/10 hover:text-ink"
                >
                  <CloseIcon className="h-3 w-3" />
                </button>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </>
  );
}
